import { Breadcrumb as AntBreadcrumb } from "antd";

import { cn } from "@btnet/utilities-core";

import "./BuiBreadcrumb.less";
import type {
  IBuiBreadcrumbItemProps,
  IBuiBreadcrumbProps,
} from "./BuiBreadcrumb.types";

/**
 * BuiBreadcrumbItem sub-component for individual breadcrumb items.
 * A styled wrapper around antd Breadcrumb.Item with full access to its API.
 * @param props - The component props.
 * @param props.testid - Test ID for the breadcrumb item.
 * @param props.className - Additional CSS class names.
 */
const BuiBreadcrumbItem = ({
  testid,
  className,
  ...props
}: IBuiBreadcrumbItemProps) => (
  <AntBreadcrumb.Item
    data-testid={testid}
    className={cn("bui-breadcrumb-item", className)}
    {...props}
  />
);

/**
 * BuiBreadcrumb component - a styled wrapper around antd Breadcrumb.
 * Provides full access to antd's Breadcrumb API with Blueprint styling.
 * For accessibility, wrap in a nav element with aria-label:
 * ```tsx
 * <nav aria-label="Breadcrumb">
 *   <BuiBreadcrumb testid="nav-breadcrumb">...</BuiBreadcrumb>
 * </nav>
 * ```
 * For the current page, add `aria-current="page"` to the element.
 * @param props - The component props.
 * @param props.testid - Test ID for the breadcrumb.
 * @param props.className - Additional CSS class names.
 * @example
 * ```tsx
 * <nav aria-label="Breadcrumb">
 *   <BuiBreadcrumb testid="nav-breadcrumb">
 *     <BuiBreadcrumb.Item testid="home">
 *       <a href="/">Home</a>
 *     </BuiBreadcrumb.Item>
 *     <BuiBreadcrumb.Item testid="products">
 *       <a href="/products">Products</a>
 *     </BuiBreadcrumb.Item>
 *     <BuiBreadcrumb.Item testid="current">
 *       <span aria-current="page">Current Page</span>
 *     </BuiBreadcrumb.Item>
 *   </BuiBreadcrumb>
 * </nav>
 * ```
 */
const BuiBreadcrumbComponent = ({
  testid,
  className,
  ...props
}: IBuiBreadcrumbProps) => (
  <AntBreadcrumb
    data-testid={testid}
    className={cn("bui-breadcrumb", className)}
    {...props}
  />
);

// Compound component pattern
export const BuiBreadcrumb = Object.assign(BuiBreadcrumbComponent, {
  Item: BuiBreadcrumbItem,
});
